import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionCard } from '@clarion/ClarionCard';
import { Icon } from '@clarion/ClarionIcon';
import { ClarionTag as Tag } from '@clarion/ClarionTag';
import Divider from '@clarion/Divider';
import { H5 } from '@clarion/Typography';
import React, { useState } from 'react';
import { useUserState } from '../auth/UserState';
import ActionButton from '../shared/ActionButton';
import { useIsAdmin } from '../workspace/permission';
import { Assignment, TimeEntry, formatTimeSpent } from './assignment';
import { deleteTimeEntry } from './assignmentService';
import TimeEntryForm from './TimeEntryForm';
import { startTimer, stopTimer } from './timeTrackingService';
import { useRefreshActiveTimer } from './useActiveTimer';
import { useTimer } from './useTimer';

type TimeTrackerProps = {
  assignment: Assignment;
  onUpdate: (updatedAssignment: Assignment) => void;
  className?: string;
  compact?: boolean;
};

export default function TimeTracker({
  assignment,
  onUpdate,
  className = '',
  compact = false,
}: TimeTrackerProps) {
  const { user } = useUserState();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddingNewEntry, setIsAddingNewEntry] = useState(false);
  const { elapsedTime, isTimerRunning, resetTimer, activeTimeEntry } = useTimer(
    assignment,
    user?.uid || '',
  );

  const refreshActiveTimer = useRefreshActiveTimer();

  const currentTime = formatTimeSpent(elapsedTime);

  const handleStartTimer = async () => {
    try {
      setIsLoading(true);
      const updatedAssignment = await startTimer(
        assignment,
        user?.uid,
        user?.displayName || user?.email || 'Unknown User',
      );

      onUpdate(updatedAssignment);
      refreshActiveTimer();
    } catch (error) {
      console.error('Error starting timer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopTimer = async () => {
    if (!user || !assignment) {
      console.error('Cannot stop timer - no user logged in or no assignment');
      return;
    }

    if (!activeTimeEntry) {
      console.error('Cannot stop timer - no active time entry');
      return;
    }

    try {
      setIsLoading(true);

      // Reset UI state immediately to improve perceived responsiveness
      resetTimer();

      // Use the shared time tracking service
      const updatedAssignment = await stopTimer(
        assignment,
        user.uid,
        user.displayName || user.email || 'Unknown User',
      );

      onUpdate(updatedAssignment);
      refreshActiveTimer();
    } catch (error) {
      console.error('Error stopping timer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const totalTimeSpent = assignment.totalTimeSpent || 0;
  const formattedTotalTime = formatTimeSpent(totalTimeSpent);

  if (compact) {
    return (
      <div className={`flex items-center ${className}`}>
        {isTimerRunning ? (
          <>
            <Tag intent="primary" className="mr-2">
              <Icon icon="time" className="mr-1" />{' '}
              <span className="tabular-nums">{currentTime}</span>
            </Tag>
            <ClarionButton
              icon="stop"
              intent="danger"
              size="small"
              variant="minimal"
              onClick={handleStopTimer}
              loading={isLoading}
              isDisabled={isLoading}
              tooltip="Stop timer"
            />
          </>
        ) : (
          <>
            <Tag minimal className="mr-2">
              <Icon icon="time" className="mr-1" /> {formattedTotalTime}
            </Tag>
            <ClarionButton
              icon="play"
              intent="success"
              size="small"
              variant="minimal"
              onClick={handleStartTimer}
              loading={isLoading}
              isDisabled={
                isLoading || assignment.status === 'completed' || assignment.status === 'cancelled'
              }
              tooltip="Start timer"
            />
          </>
        )}
      </div>
    );
  }

  return (
    <ClarionCard className={className}>
      <div className="flex justify-between items-center mb-3">
        <H5 className="mb-0">Time Tracking</H5>
        <Tag intent="primary" minimal icon="timeline-bar-chart" large>
          Total: {formattedTotalTime}
        </Tag>
      </div>

      <div className="flex flex-col items-center mb-3">
        {isTimerRunning ? (
          <>
            <div className="text-2xl font-bold mb-2 tabular-nums">{currentTime}</div>
            <div className="flex">
              <ClarionButton
                icon="stop"
                intent="danger"
                onClick={handleStopTimer}
                loading={isLoading}
                isDisabled={isLoading}
                className="mr-2"
              >
                Stop Timer
              </ClarionButton>
            </div>
          </>
        ) : (
          <>
            <div className="text-xl opacity-70 mb-2">Timer not running</div>
            <ClarionButton
              icon="play"
              intent="success"
              onClick={handleStartTimer}
              loading={isLoading}
              isDisabled={
                isLoading || assignment.status === 'completed' || assignment.status === 'cancelled'
              }
            >
              Start timer
            </ClarionButton>
          </>
        )}
      </div>

      <Divider className="mb-3" />
      <div className="flex justify-between items-center mb-2">
        <div className="font-medium">Time Entries</div>
        <ClarionButton
          icon="plus"
          size="small"
          variant="minimal"
          intent="primary"
          tooltip="Add manual time entry"
          onClick={() => setIsAddingNewEntry(true)}
        />
      </div>
      <div className="max-h-60 overflow-y-auto">
        {assignment.timeEntries?.map((entry) => (
          <TimeEntryItem key={entry.id} entry={entry} assignment={assignment} onUpdate={onUpdate} />
        ))}
      </div>

      {isAddingNewEntry && (
        <TimeEntryForm
          isOpen
          onClose={() => setIsAddingNewEntry(false)}
          assignment={assignment}
          onEntryAdded={onUpdate}
        />
      )}
    </ClarionCard>
  );
}

type TimeEntryItemProps = {
  entry: TimeEntry;
  assignment: Assignment;
  onUpdate: (updatedAssignment: Assignment) => void;
};

const TimeEntryItem: React.FC<TimeEntryItemProps> = ({ entry, assignment, onUpdate }) => {
  const { user } = useUserState();
  const isAdmin = useIsAdmin();
  const [isEditing, setIsEditing] = useState(false);

  // Calculate duration
  let duration = 0;
  if (entry.duration) {
    duration = entry.duration;
  } else if (entry.startTime && entry.endTime) {
    duration = entry.endTime - entry.startTime;
  }

  const formattedDuration = formatTimeSpent(duration);

  // Format dates
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleDelete = async () => {
    if (!user) return;
    const updatedAssignment = await deleteTimeEntry(
      assignment.id,
      assignment.workspaceId,
      entry.id,
      user.uid,
      user.displayName || user.email || 'Unknown User',
    );

    // Make a deep copy of the updated assignment
    const updatedAssignmentCopy = JSON.parse(JSON.stringify(updatedAssignment));
    onUpdate(updatedAssignmentCopy);
  };

  // Check if user can edit or delete this time entry
  // User can delete if: it's their own entry OR they are an admin
  const canEdit = user && (entry.userId === user.uid || isAdmin);

  return (
    <>
      <div className="p-2 mb-2 border border-gray-400 dark:border-gray-600 rounded-sm bg-gray-50 dark:bg-gray-800">
        <div className="flex justify-between items-center">
          <div className="font-medium">{formattedDuration}</div>
          {entry.isManualEntry && <Tag minimal>Manual</Tag>}
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {entry.startTime && (
            <div>
              <span className="font-medium">Start:</span> {formatDate(entry.startTime)}
            </div>
          )}
          {entry.endTime && (
            <div>
              <span className="font-medium">End:</span> {formatDate(entry.endTime)}
            </div>
          )}
        </div>
        {entry.notes && <div className="mt-1 text-sm">{entry.notes}</div>}
        <div className="flex justify-between items-center mt-2">
          <div className="text-xs text-gray-500 dark:text-gray-300">
            Tracked by: {entry.userName}
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <ClarionButton
                icon="edit"
                size="small"
                variant="minimal"
                intent="primary"
                tooltip="Edit time entry"
                onClick={() => setIsEditing(true)}
              />
            )}
            {canEdit && (
              <ActionButton
                icon="trash"
                size="small"
                variant="minimal"
                intent="danger"
                tooltip="Delete time entry"
                action={handleDelete}
              />
            )}
          </div>
        </div>
      </div>
      {isEditing && (
        <TimeEntryForm
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          assignment={assignment}
          existingEntry={entry}
          onEntryAdded={onUpdate}
        />
      )}
    </>
  );
};
